/**
 * City districts — cross streets and side neighborhoods off the main block:
 * Downtown Strip (east of Neon Row), Ironworks District (west of Autumn Lane),
 * Boardwalk Beach (east of the Fairgrounds). Plus the walk/drive network and manholes.
 * All drawing is in world coords (ctx pre-translated by -camX; y passed through -cam).
 */

export const CAM_MIN_X = -1400;
export const CAM_MAX_X = 1400;

// walkable corridors (union of rects)
export const WALK_RECTS = [
  { x1: 245, y1: 240, x2: 555, y2: 5530 },        // main street
  { x1: 15, y1: 470, x2: 245, y2: 4720 },         // left lawns — houses + mansion rows, walk all around
  { x1: 555, y1: 470, x2: 785, y2: 4720 },        // right lawns — houses + mansion rows, walk all around
  { x1: 245, y1: 690, x2: 2080, y2: 800 },        // downtown cross road
  { x1: 850, y1: 590, x2: 2090, y2: 940 },        // downtown plaza
  { x1: -1330, y1: 1925, x2: 555, y2: 2035 },     // ironworks cross road
  { x1: -1360, y1: 1800, x2: -280, y2: 2280 },    // ironworks yard
  { x1: 245, y1: 4720, x2: 2140, y2: 4840 },      // boardwalk cross road
  { x1: 900, y1: 4580, x2: 2140, y2: 5360 },      // beach + boardwalk
  { x1: 245, y1: 5150, x2: 800, y2: 5420 },       // fairgrounds plaza / Turtle Shack forecourt
  { x1: 245, y1: 5380, x2: 640, y2: 5640 },       // path down to Sunset Shore
  { x1: 40, y1: 5560, x2: 760, y2: 6040 },        // Sunset Shore: amphitheater + sand
  { x1: 60, y1: 6020, x2: 740, y2: 6320 },        // ocean (swimmable)
];

// solid house footprints (1-16) — you walk around them, not through them
export const HOUSE_BLOCKS = Array.from({ length: 16 }, (_, i) => {
  const n = i + 1;
  const nb = Math.floor((n - 1) / 4);
  const idx = (n - 1) % 4;
  const row = Math.floor(idx / 2);
  const side = idx % 2;
  const hx = side ? 675 : 125;
  const hy = 400 + nb * 640 + 210 + row * 300;
  return { x1: hx - 70, y1: hy - 100, x2: hx + 70, y2: hy + 4 };
}).concat([
  // mansion footprints (walk around them, doors stay reachable below)
  { x1: 35, y1: 3180, x2: 245, y2: 3334 },   // grand mansion (17)
  { x1: 545, y1: 3160, x2: 775, y2: 3484 },  // owner's 5-story mansion (0)
  { x1: 35, y1: 3810, x2: 245, y2: 3964 },   // elite mansion I (18)
  { x1: 555, y1: 3810, x2: 765, y2: 3964 },  // elite mansion II (19)
  { x1: 35, y1: 4250, x2: 245, y2: 4404 },   // admin mansion I (20)
  { x1: 555, y1: 4250, x2: 765, y2: 4404 },  // admin mansion II (21)
]);

// drivable network (a bit wider on the main road; you can cruise the districts too)
export const DRIVE_RECTS = [
  { x1: 200, y1: 300, x2: 600, y2: 5520 },
  { x1: 200, y1: 690, x2: 2070, y2: 800 },
  { x1: 860, y1: 600, x2: 2070, y2: 930 },
  { x1: -1320, y1: 1925, x2: 600, y2: 2035 },
  { x1: -1340, y1: 1810, x2: -300, y2: 2270 },
  { x1: 200, y1: 4720, x2: 2130, y2: 4840 },
  { x1: 910, y1: 4600, x2: 2120, y2: 5340 },
];

export const MANHOLES = [
  { id: 'club', x: 350, y: 470, label: 'Club Street' },
  { id: 'palm', x: 450, y: 1350, label: 'Palm Court' },
  { id: 'iron', x: -700, y: 1980, label: 'Ironworks' },
  { id: 'fair', x: 455, y: 5290, label: 'Fairgrounds' },
];

// horizontal strolling NPCs in the districts
export const H_NPCS = [
  { y: 748, xMin: 620, xMax: 1980, speed: 0.5, phase: 300, char: 'pirate' },
  { y: 778, xMin: 900, xMax: 2000, speed: 0.38, phase: 900, char: 'mage' },
  { y: 1972, xMin: -1200, xMax: 180, speed: 0.45, phase: 150, char: 'firefighter' },
  { y: 4772, xMin: 620, xMax: 2040, speed: 0.52, phase: 600, char: 'surfer' },
  { y: 5060, xMin: 960, xMax: 2020, speed: 0.34, phase: 50, char: 'ballerina' },
];

export const insideRects = (x, y, rects) =>
  rects.some((r) => x >= r.x1 && x <= r.x2 && y >= r.y1 && y <= r.y2);

const canStand = (x, y, rects, blocks) =>
  insideRects(x, y, rects) && !(blocks && blocks.length && insideRects(x, y, blocks));

// axis-sliding movement clamp against the rect union (blocks = solid obstacles)
export const clampToRects = (nx, ny, ox, oy, rects, blocks) => {
  if (canStand(nx, ny, rects, blocks)) return { x: nx, y: ny };
  if (canStand(nx, oy, rects, blocks)) return { x: nx, y: oy };
  if (canStand(ox, ny, rects, blocks)) return { x: ox, y: ny };
  return { x: ox, y: oy };
};

// snap an arbitrary tap point into the nearest walkable rect (nudged off blocks)
export const clampPointToRects = (x, y, rects, blocks) => {
  if (canStand(x, y, rects, blocks)) return { x, y };
  let best = null;
  let bestD = Infinity;
  rects.forEach((r) => {
    const cx = Math.max(r.x1, Math.min(r.x2, x));
    const cy = Math.max(r.y1, Math.min(r.y2, y));
    const d = (cx - x) ** 2 + (cy - y) ** 2;
    if (d < bestD) { bestD = d; best = { x: cx, y: cy }; }
  });
  let pt = best || { x, y };
  if (blocks && blocks.length) {
    const hit = blocks.find((b) => pt.x >= b.x1 && pt.x <= b.x2 && pt.y >= b.y1 && pt.y <= b.y2);
    if (hit) pt = { x: pt.x, y: hit.y2 + 10 };
  }
  return pt;
};

const roadAsphalt = '#26262c';
const roadCurb = '#3f4048';

const drawCrossRoad = (ctx, cam, x1, x2, y1, y2) => {
  ctx.fillStyle = roadCurb;
  ctx.fillRect(x1, y1 - 12 - cam, x2 - x1, 12);
  ctx.fillRect(x1, y2 - cam, x2 - x1, 12);
  ctx.fillStyle = roadAsphalt;
  ctx.fillRect(x1, y1 - cam, x2 - x1, y2 - y1);
  ctx.strokeStyle = 'rgba(250,204,21,0.8)';
  ctx.lineWidth = 3;
  ctx.setLineDash([26, 22]);
  const my = (y1 + y2) / 2 - cam;
  ctx.beginPath(); ctx.moveTo(x1, my); ctx.lineTo(x2, my); ctx.stroke();
  ctx.setLineDash([]);
};

export function drawManholes(ctx, cam, ph) {
  MANHOLES.forEach((m) => {
    const y = m.y - cam;
    if (y < -30 || y > 5000) { /* fallthrough */ }
    ctx.fillStyle = '#17181d';
    ctx.beginPath(); ctx.ellipse(m.x, y, 15, 9, 0, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#4a4c55';
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.ellipse(m.x, y, 15, 9, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = 'rgba(120,124,138,0.8)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.ellipse(m.x, y, 10, 5.6, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(m.x - 8, y); ctx.lineTo(m.x + 8, y); ctx.stroke();
    // faint green glow seeping out — the secret hint
    const gl = 0.25 + Math.abs(Math.sin(ph * 0.02 + m.x)) * 0.3;
    ctx.fillStyle = `rgba(52,211,153,${gl * 0.35})`;
    ctx.beginPath(); ctx.ellipse(m.x, y, 18, 11, 0, 0, Math.PI * 2); ctx.fill();
  });
}

// ===== DOWNTOWN STRIP (east of Neon Row, y 590-940) =====
export function drawDowntown(ctx, cam, ph, t) {
  const top = 590 - cam;
  if (top > 1500 || top + 350 < 0) return;
  // plaza ground
  ctx.fillStyle = '#191722';
  ctx.fillRect(850, top, 1240, 350);
  ctx.strokeStyle = 'rgba(255,255,255,0.05)';
  ctx.lineWidth = 1;
  for (let gx = 900; gx < 2090; gx += 80) {
    ctx.beginPath(); ctx.moveTo(gx, top); ctx.lineTo(gx, top + 350); ctx.stroke();
  }
  drawCrossRoad(ctx, cam, 555, 2080, 690, 800);
  // storefronts (north row)
  const shops = [
    { x: 900, w: 200, name: 'PIZZA PALACE', icon: '🍕', c: '#ef4444', awn: '#ef4444' },
    { x: 1120, w: 200, name: 'RETRO ARCADE', icon: '🕹️', c: '#a78bfa', awn: '#8b5cf6' },
    { x: 1340, w: 200, name: 'VINYL VAULT', icon: '🎵', c: '#38bdf8', awn: '#0ea5e9' },
    { x: 1560, w: 200, name: 'INK & IRON', icon: '⚡', c: '#f472b6', awn: '#111' },
    { x: 1780, w: 220, name: 'BOBA BREAK', icon: '🧋', c: '#fbbf24', awn: '#f59e0b' },
  ];
  shops.forEach((sh, i) => {
    const bx = sh.x;
    const by = 660 - cam; // building base sits on north sidewalk
    // building
    ctx.fillStyle = '#221e30';
    ctx.fillRect(bx, by - 150, sh.w, 138);
    ctx.strokeStyle = 'rgba(0,0,0,0.4)';
    ctx.strokeRect(bx, by - 150, sh.w, 138);
    // windows glow
    const gl = 0.5 + Math.abs(Math.sin(t * 1.1 + i)) * 0.5;
    ctx.fillStyle = `rgba(253,230,138,${0.25 + gl * 0.2})`;
    ctx.fillRect(bx + 14, by - 96, sh.w - 90, 46);
    // door
    ctx.fillStyle = '#141020';
    ctx.fillRect(bx + sh.w - 58, by - 74, 40, 62);
    // awning stripes
    ctx.fillStyle = sh.awn;
    ctx.fillRect(bx - 4, by - 104, sh.w + 8, 12);
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    for (let a = 0; a < (sh.w + 8) / 24; a++) ctx.fillRect(bx - 4 + a * 24, by - 104, 12, 12);
    // neon sign
    ctx.font = 'bold 15px monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = sh.c;
    ctx.shadowColor = sh.c;
    ctx.shadowBlur = 8 + gl * 8;
    ctx.fillText(sh.name, bx + sh.w / 2, by - 122);
    ctx.shadowBlur = 0;
  });
  // south side props: benches, planters, hydrant
  [960, 1300, 1640, 1960].forEach((bx2) => {
    const by2 = 880 - cam;
    ctx.fillStyle = '#3a2e20';
    ctx.fillRect(bx2, by2, 56, 8);
    ctx.fillRect(bx2 + 4, by2 + 8, 6, 10);
    ctx.fillRect(bx2 + 46, by2 + 8, 6, 10);
  });
  [1140, 1480, 1820].forEach((px2) => {
    const py2 = 884 - cam;
    ctx.fillStyle = '#2e2a3c';
    ctx.fillRect(px2, py2, 26, 16);
    ctx.fillStyle = '#166534';
    ctx.beginPath(); ctx.arc(px2 + 13, py2 - 4, 12, Math.PI, 0); ctx.fill();
  });
  ctx.fillStyle = '#dc2626';
  ctx.fillRect(880, 872 - cam, 10, 16);
  ctx.fillRect(877, 876 - cam, 16, 4);
  // district banner
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(1200, 620 - cam - 14, 400, 24);
  ctx.font = 'bold 14px monospace';
  ctx.textAlign = 'center';
  ctx.fillStyle = '#a78bfa';
  ctx.shadowColor = '#a78bfa'; ctx.shadowBlur = 10;
  ctx.fillText('✦ DOWNTOWN STRIP ✦', 1400, 620 - cam + 3);
  ctx.shadowBlur = 0;
}

// ===== IRONWORKS DISTRICT (west of Autumn Lane, y 1800-2280) =====
export function drawIronworks(ctx, cam, ph, t) {
  const top = 1800 - cam;
  if (top > 1500 || top + 480 < 0) return;
  ctx.fillStyle = '#1b1a17';
  ctx.fillRect(-1360, top, 1080, 480);
  drawCrossRoad(ctx, cam, -1330, 245, 1925, 2035);
  // warehouses (north side)
  [[-1280, 420, '#3d3a33', 'WAREHOUSE 7'], [-800, 380, '#38332c', 'RZNT FREIGHT']].forEach(([wx, ww, c, label]) => {
    const wy = 1913 - cam;
    ctx.fillStyle = c;
    ctx.fillRect(wx, wy - 120, ww, 112);
    ctx.fillStyle = '#26231e';
    ctx.beginPath();
    ctx.moveTo(wx - 10, wy - 120); ctx.lineTo(wx + ww / 2, wy - 150); ctx.lineTo(wx + ww + 10, wy - 120);
    ctx.closePath(); ctx.fill();
    // corrugated door
    ctx.fillStyle = '#4c463c';
    ctx.fillRect(wx + ww / 2 - 40, wy - 70, 80, 62);
    ctx.strokeStyle = 'rgba(0,0,0,0.4)';
    ctx.lineWidth = 1;
    for (let d = 0; d < 8; d++) {
      ctx.beginPath(); ctx.moveTo(wx + ww / 2 - 40, wy - 70 + d * 8); ctx.lineTo(wx + ww / 2 + 40, wy - 70 + d * 8); ctx.stroke();
    }
    ctx.font = 'bold 12px monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#fb923c';
    ctx.fillText(label, wx + ww / 2, wy - 128);
  });
  // smokestack + animated smoke
  const sx = -330;
  const sy = 1910 - cam;
  ctx.fillStyle = '#4a4038';
  ctx.fillRect(sx, sy - 210, 40, 205);
  ctx.fillStyle = '#5c5248';
  ctx.fillRect(sx - 4, sy - 214, 48, 12);
  for (let i = 0; i < 4; i++) {
    const cyc = (ph * 0.35 + i * 40) % 160;
    ctx.fillStyle = `rgba(150,150,155,${Math.max(0, 0.34 - cyc / 480)})`;
    ctx.beginPath();
    ctx.arc(sx + 20 + Math.sin((cyc + i * 30) * 0.04) * 14, sy - 218 - cyc * 0.8, 12 + cyc * 0.16, 0, Math.PI * 2);
    ctx.fill();
  }
  // crates + barrels (south yard)
  [[-1240, 2110], [-1190, 2110], [-1215, 2078], [-620, 2140], [-560, 2160]].forEach(([cx2, cy2]) => {
    const yy = cy2 - cam;
    ctx.fillStyle = '#5a4526';
    ctx.fillRect(cx2, yy, 44, 36);
    ctx.strokeStyle = '#3a2c16';
    ctx.strokeRect(cx2, yy, 44, 36);
    ctx.beginPath(); ctx.moveTo(cx2, yy); ctx.lineTo(cx2 + 44, yy + 36); ctx.moveTo(cx2 + 44, yy); ctx.lineTo(cx2, yy + 36); ctx.stroke();
  });
  [[-880, 2130], [-840, 2130], [-860, 2098]].forEach(([bx2, by2]) => {
    const yy = by2 - cam;
    ctx.fillStyle = '#6b2222';
    ctx.fillRect(bx2, yy, 26, 34);
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.strokeRect(bx2, yy, 26, 34);
    ctx.fillStyle = '#874444';
    ctx.fillRect(bx2, yy + 8, 26, 4);
    ctx.fillRect(bx2, yy + 22, 26, 4);
  });
  // flickering sodium lamp
  const fl = Math.abs(Math.sin(t * 7)) > 0.15 ? 1 : 0.2;
  ctx.strokeStyle = '#3a3a44'; ctx.lineWidth = 4;
  ctx.beginPath(); ctx.moveTo(-500, 2120 - cam); ctx.lineTo(-500, 2050 - cam); ctx.stroke();
  ctx.fillStyle = `rgba(251,146,60,${0.9 * fl})`;
  ctx.beginPath(); ctx.arc(-500, 2046 - cam, 5, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = `rgba(251,146,60,${0.08 * fl})`;
  ctx.beginPath(); ctx.arc(-500, 2056 - cam, 40, 0, Math.PI * 2); ctx.fill();
  // sparks near warehouse 7
  if (Math.floor(ph / 7) % 9 < 2) {
    for (let i = 0; i < 4; i++) {
      ctx.fillStyle = i % 2 ? '#fde047' : '#fb923c';
      ctx.fillRect(-1000 + (i * 7) % 20, 1990 - cam - (ph % 12) - i * 3, 2, 2);
    }
  }
  // banner
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(-800, 1860 - cam - 14, 360, 24);
  ctx.font = 'bold 14px monospace';
  ctx.textAlign = 'center';
  ctx.fillStyle = '#fb923c';
  ctx.shadowColor = '#fb923c'; ctx.shadowBlur = 10;
  ctx.fillText('⚙ IRONWORKS DISTRICT ⚙', -620, 1860 - cam + 3);
  ctx.shadowBlur = 0;
}

// ===== BOARDWALK BEACH (east of the Fairgrounds, y 4580-5360) =====
export function drawBoardwalk(ctx, cam, ph, t) {
  const top = 4580 - cam;
  if (top > 1500 || top + 780 < 0) return;
  // sand
  ctx.fillStyle = '#d9bd8a';
  ctx.fillRect(900, top, 850, 780);
  // ocean (east edge) with animated waves
  ctx.fillStyle = '#155e75';
  ctx.fillRect(1750, top, 400, 780);
  ctx.fillStyle = '#0e7490';
  ctx.fillRect(1750, top, 60 + Math.sin(t * 0.8) * 14, 780);
  for (let i = 0; i < 8; i++) {
    const wy = 4620 + i * 96 - cam;
    const wave = Math.sin(t * 1.2 + i) * 16;
    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(1750 + wave, wy);
    ctx.quadraticCurveTo(1780 + wave, wy + 24, 1752 + wave, wy + 48);
    ctx.stroke();
  }
  // wooden boardwalk strip
  ctx.fillStyle = '#8a6239';
  ctx.fillRect(900, top, 120, 780);
  ctx.strokeStyle = 'rgba(0,0,0,0.25)';
  ctx.lineWidth = 1;
  for (let by = 0; by < 780; by += 22) {
    ctx.beginPath(); ctx.moveTo(900, top + by); ctx.lineTo(1020, top + by); ctx.stroke();
  }
  drawCrossRoad(ctx, cam, 555, 2140, 4720, 4840);
  // palms
  [[1080, 4900], [1420, 4640], [1660, 5160], [1200, 5280]].forEach(([px2, py2], i) => {
    const yy = py2 - cam;
    ctx.strokeStyle = '#8a6a3a'; ctx.lineWidth = 6;
    ctx.beginPath(); ctx.moveTo(px2, yy); ctx.quadraticCurveTo(px2 + 8, yy - 30, px2 + 4, yy - 58); ctx.stroke();
    ctx.fillStyle = '#2e8b57';
    [-1, -0.3, 0.4, 1].forEach((d) => {
      ctx.beginPath();
      ctx.ellipse(px2 + 4 + d * 16, yy - 62 + Math.abs(d) * 4, 17, 6, d * 0.55 + Math.sin(t + i) * 0.05, 0, Math.PI * 2);
      ctx.fill();
    });
  });
  // umbrellas + towels + chairs
  [[1180, 4960, '#f472b6'], [1440, 5080, '#38bdf8'], [1300, 5220, '#fde047']].forEach(([ux, uy, c]) => {
    const yy = uy - cam;
    ctx.fillStyle = 'rgba(0,0,0,0.18)';
    ctx.beginPath(); ctx.ellipse(ux, yy + 6, 26, 8, 0, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#d1d5db'; ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.moveTo(ux, yy); ctx.lineTo(ux, yy - 34); ctx.stroke();
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.arc(ux, yy - 34, 20, Math.PI, 0); ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.beginPath(); ctx.arc(ux, yy - 34, 20, Math.PI + 0.4, Math.PI + 1); ctx.fill();
    // towel
    ctx.fillStyle = c;
    ctx.globalAlpha = 0.55;
    ctx.fillRect(ux + 24, yy - 6, 26, 40);
    ctx.globalAlpha = 1;
  });
  // sandcastle
  const scy = 5140 - cam;
  ctx.fillStyle = '#c9a877';
  ctx.fillRect(1560, scy, 52, 30);
  ctx.fillRect(1552, scy - 16, 16, 46);
  ctx.fillRect(1604, scy - 16, 16, 46);
  ctx.fillStyle = '#b8935e';
  ctx.fillRect(1552, scy - 20, 16, 5);
  ctx.fillRect(1604, scy - 20, 16, 5);
  ctx.fillStyle = '#ef4444';
  ctx.beginPath(); ctx.moveTo(1560, scy - 30); ctx.lineTo(1560, scy - 18); ctx.lineTo(1572, scy - 24); ctx.closePath(); ctx.fill();
  // volleyball net
  const vy = 4920 - cam;
  ctx.strokeStyle = '#e5e0d0'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(1500, vy); ctx.lineTo(1500, vy - 40); ctx.moveTo(1640, vy); ctx.lineTo(1640, vy - 40); ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,0.55)'; ctx.lineWidth = 1;
  for (let n = 0; n < 4; n++) {
    ctx.beginPath(); ctx.moveTo(1500, vy - 36 + n * 7); ctx.lineTo(1640, vy - 36 + n * 7); ctx.stroke();
  }
  // seagulls
  for (let i = 0; i < 3; i++) {
    const gx = 1100 + ((ph * (0.7 + i * 0.2) + i * 300) % 900);
    const gy = 4640 + i * 60 - cam + Math.sin(ph * 0.05 + i) * 10;
    ctx.strokeStyle = 'rgba(255,255,255,0.8)';
    ctx.lineWidth = 2;
    const flap = Math.sin(ph * 0.2 + i * 2) * 4;
    ctx.beginPath();
    ctx.moveTo(gx - 7, gy - flap); ctx.quadraticCurveTo(gx, gy + 3, gx + 7, gy - flap);
    ctx.stroke();
  }
  // banner
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(1150, 4700 - cam - 40, 420, 24);
  ctx.font = 'bold 14px monospace';
  ctx.textAlign = 'center';
  ctx.fillStyle = '#fde047';
  ctx.shadowColor = '#fde047'; ctx.shadowBlur = 10;
  ctx.fillText('🌴 BOARDWALK BEACH 🌴', 1360, 4700 - cam - 23);
  ctx.shadowBlur = 0;
}

// striped barricade + sign for road ends that lead nowhere (yet)
const drawComingSoon = (ctx, cam, x, y) => {
  const yS = y - cam;
  if (yS < -160 || yS > 1500) return;
  // support legs
  ctx.fillStyle = '#3a3d46';
  ctx.fillRect(x - 52, yS - 6, 8, 26);
  ctx.fillRect(x + 44, yS - 6, 8, 26);
  // striped barrier board
  ctx.save();
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(x - 60, yS - 26, 120, 22);
  ctx.beginPath();
  ctx.rect(x - 60, yS - 26, 120, 22);
  ctx.clip();
  ctx.fillStyle = '#1c1c22';
  for (let i = -3; i < 6; i++) {
    ctx.beginPath();
    ctx.moveTo(x - 60 + i * 24, yS - 26);
    ctx.lineTo(x - 60 + i * 24 + 12, yS - 26);
    ctx.lineTo(x - 60 + i * 24 - 10, yS - 4);
    ctx.lineTo(x - 60 + i * 24 - 22, yS - 4);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
  ctx.strokeStyle = 'rgba(0,0,0,0.5)';
  ctx.lineWidth = 2;
  ctx.strokeRect(x - 60, yS - 26, 120, 22);
  // sign above
  ctx.fillStyle = '#14161d';
  ctx.fillRect(x - 74, yS - 62, 148, 30);
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 2;
  ctx.strokeRect(x - 74, yS - 62, 148, 30);
  ctx.font = 'bold 13px monospace';
  ctx.textAlign = 'center';
  ctx.fillStyle = '#fbbf24';
  ctx.fillText('🚧 COMING SOON 🚧', x, yS - 42);
  // little cones
  [[-84, 6], [84, 6]].forEach(([dx2, dy2]) => {
    ctx.fillStyle = '#f97316';
    ctx.beginPath();
    ctx.moveTo(x + dx2, yS + dy2 - 14);
    ctx.lineTo(x + dx2 - 7, yS + dy2);
    ctx.lineTo(x + dx2 + 7, yS + dy2);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.fillRect(x + dx2 - 3.5, yS + dy2 - 7, 7, 2.6);
  });
};

export function drawDistricts(ctx, cam, ph, t) {
  drawDowntown(ctx, cam, ph, t);
  drawIronworks(ctx, cam, ph, t);
  drawBoardwalk(ctx, cam, ph, t);
  drawManholes(ctx, cam, ph);
  // dead-end roads — more city on the way
  drawComingSoon(ctx, cam, 2040, 760);   // downtown strip east end
  drawComingSoon(ctx, cam, -1290, 1992); // ironworks west end
  drawComingSoon(ctx, cam, 2100, 4792);  // boardwalk road east end
}
